import { API_BASE, API_V2_BASE } from '../config';
import { getAccessToken, getAuthHeaders, refreshAccessToken } from './authUtils';
import { toRef, refToId, refToFullObject, refToName } from './handbookRef';
import { isProtectiveForestsCnl } from '../components/forms/Form2/utils';
import { activitiesRequiredForSave, hasRecommendedMeasuresFromCard } from './recommendedMeasures';
import { validateCardRules } from './cardValidation';
import { calculateIZdor } from '../components/forms/Form4/utils';
import { mobilePlotArea } from './mobileCalculations';
import { doubleOrNil } from './mpOchags';
import { resolveForestAddress } from './forestAddress';

/**
 * Форматирует дату в формат DD.MM.YYYY
 */
const formatDate = (date) => {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

/**
 * Преобразует дату из формата DD.MM.YYYY в формат yyyy-MM-dd (для input type="date")
 */
const convertDateToInputFormat = (dateString) => {
  if (!dateString) return '';
  
  // Если дата уже в формате yyyy-MM-dd, возвращаем как есть
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  
  // Если дата в формате DD.MM.YYYY, преобразуем в yyyy-MM-dd
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateString)) {
    const [day, month, year] = dateString.split('.');
    return `${year}-${month}-${day}`;
  }
  
  // Если это объект Date или строка, которую можно распарсить
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  } catch (e) {
    // Игнорируем ошибки парсинга
  }
  
  return '';
};

/**
 * Получить сегодняшнюю дату в формате DD.MM.YYYY
 */
const getTodayDate = () => {
  return formatDate(new Date());
};

/**
 * Преобразует координаты из формата {lat, lng} в строки
 */
const formatCoordinates = (coord) => {
  if (!coord) return '';
  if (typeof coord === 'object') {
    // Если это объект с полями lat и lng
    if (coord.lat !== undefined) {
      return String(coord.lat);
    }
    if (coord.lng !== undefined) {
      return String(coord.lng);
    }
    return '';
  }
  return String(coord);
};

/**
 * Ищет запись в справочнике по названию
 */
const findReferenceByName = (name, handbookId, handbooks) => {
  if (!name) return null;
  
  const handbook = handbooks.find(h => h.id === handbookId);
  if (!handbook || !handbook.references) return null;
  
  // Нормализуем название для сравнения (убираем лишние пробелы, приводим к нижнему регистру)
  const normalizedName = String(name).trim().toLowerCase();
  
  const reference = handbook.references.find(r => {
    const refName = String(r.name || '').trim().toLowerCase();
    return refName === normalizedName || 
           String(r.name || '') === String(name) ||
           String(r.name || '').trim() === String(name).trim();
  });
  
  if (reference) {
    return {
      id: reference.id,
      name: reference.name || '',
      ...Object.fromEntries(
        Object.entries(reference).filter(([key]) => key !== 'id' && key !== 'name')
      )
    };
  }
  
  return null;
};

const preserveReferenceValue = (value, handbookId, handbooks) => {
  if (!value) return null;

  if (typeof value === 'object') {
    if (value.id === undefined || value.id === null || value.id === '') {
      return null;
    }

    const refById = getHandbookReference(value.id, handbookId, handbooks);
    if (refById) {
      return refById;
    }

    const refByName = value.name ? findReferenceByName(value.name, handbookId, handbooks) : null;
    if (refByName) {
      return refByName;
    }

    return {
      id: value.id,
      name: value.name || '',
      ...Object.fromEntries(
        Object.entries(value).filter(([key]) => key !== 'id' && key !== 'name')
      )
    };
  }

  const raw = String(value).trim();
  if (!raw) return null;

  if (!isNaN(raw)) {
    return getHandbookReference(raw, handbookId, handbooks) || { id: raw, name: '' };
  }

  return findReferenceByName(raw, handbookId, handbooks) || { name: raw };
};

/**
 * Преобразует данные Form1 в формат для сохранения
 */
export const transformForm1 = (form1Data, handbooks = []) => {
  const today = getTodayDate();
  
  const { lvo: lvoObj, ulvo: ulvoObj, urc: urcObj, isUrcRequired } = resolveForestAddress(form1Data, handbooks);
  const lvoHandbookId = 36;
  const ulvoHandbookId = 37;
  const urcHandbookId = 38;
  
  // Преобразуем координаты: lat может быть объектом {lat, lng} или строкой
  const latValue = typeof form1Data.lat === 'object' && form1Data.lat?.lat !== undefined 
    ? form1Data.lat.lat 
    : (typeof form1Data.lat === 'object' && form1Data.lat?.lng !== undefined 
      ? form1Data.lat.lng 
      : form1Data.lat);
  const lonValue = typeof form1Data.lon === 'object' && form1Data.lon?.lng !== undefined 
    ? form1Data.lon.lng 
    : (typeof form1Data.lon === 'object' && form1Data.lon?.lat !== undefined 
      ? form1Data.lon.lat 
      : form1Data.lon);
  
  // Для зависимых полей (filial, lvo, ulvo, urc) ищем по названию в справочниках
  // filial - Branches (id=15)
  const filialObj = form1Data.filial 
    ? (findReferenceByName(form1Data.filial, 15, handbooks) ||
       (typeof form1Data.filial === 'string' && !isNaN(form1Data.filial) 
         ? getHandbookReference(form1Data.filial, 15, handbooks) 
         : null))
    : null;
  
  // srf - Subjects (id=12) - может быть ID или строкой
  const srfObj = preserveReferenceValue(form1Data.srf, 12, handbooks);
  
  // cel - VNNGoals (id=19)
  const celObj = form1Data.cel 
    ? getHandbookReference(form1Data.cel, 19, handbooks) 
    : null;
  
  // ekolzona - EcologicalZones (id=18)
  const ekolzonaObj = form1Data.ekolzona 
    ? getHandbookReference(form1Data.ekolzona, 18, handbooks) 
    : null;
  
  return {
    latitude: latValue ? String(latValue) : '',
    longitude: lonValue ? String(lonValue) : '',
    vnum: form1Data.vnum || '',
    filial: toRef(filialObj, 15, handbooks),
    srf: toRef(srfObj, 12, handbooks),
    lvo: toRef(lvoObj, lvoHandbookId, handbooks),
    ulvo: toRef(ulvoObj, ulvoHandbookId, handbooks),
    // Как в МП: урочище обязательно, если у участкового лесничества в справочнике
    // есть дочерние записи справочника 38. Флаг сохраняется для мобильного приложения.
    isUrcRequired,
    urc: toRef(urcObj, urcHandbookId, handbooks),
    kv: form1Data.kv || '',
    kadnomer: form1Data.kadnomer || '',
    // Вариант кадастрового номера, как переключатель в МП: 6 цифр в третьей группе
    // вместо 7. Отдельного переключателя в форме нет — определяем по самому номеру.
    useSixNumberKadnomer: /^\d{2}:\d{2}:\d{6}:\d{4}$/.test(form1Data.kadnomer || ''),
    vid: form1Data.vid || '', // vid - это номер лесотаксационного выдела (поле "№" в разделе лесотаксационный выдел)
    s: form1Data.sga || '', // s - это S, га (площадь лесотаксационного выдела)
    // Пустой № лесопатологического выдела форма показывает как «1» — так и сохраняем (как в МП)
    lpvid: form1Data.lpvid || '1',
    slp: form1Data.slp || '',
    // data — дата создания карточки: проставляется один раз и дальше переносится
    // как есть (она уже в ДД.ММ.ГГГГ, повторное форматирование её ломало).
    data: form1Data.data || today,
    dataizm: today,
    cel: toRef(celObj, 19, handbooks),
    // datar — дата проведения ВНН, её вводит пользователь. Пустое значение
    // оставляем пустым: раньше сюда безусловно писалась сегодняшняя дата и
    // затирала дату работ, проставленную в мобильном приложении.
    datar: form1Data.datar ? formatDate(form1Data.datar) : '',
    ekolzona: toRef(ekolzonaObj, 18, handbooks),
    nmh: form1Data.nmh || '',
    lmh: form1Data.lmh || '',
    itaks: form1Data.itaks1 || ''
  };
};

/**
 * Получает полные данные записи из справочника по ID справочника и ID записи
 */
const getHandbookReference = (referenceId, handbookId, handbooks) => {
  if (!referenceId) return null;
  
  const handbook = handbooks.find(h => h.id === handbookId);
  if (!handbook || !handbook.references) return null;
  
  const reference = handbook.references.find(r => r.id.toString() === String(referenceId));
  if (!reference) return null;
  
  // Возвращаем полный объект со всеми полями
  return {
    id: reference.id,
    name: reference.name || '',
    ...Object.fromEntries(
      Object.entries(reference).filter(([key]) => key !== 'id' && key !== 'name')
    )
  };
};

// Справочники причин повреждения и признаков повреждения.
const PRICHINA_HANDBOOK_ID = 10; // CausesOfDamage
const PRIZN_HANDBOOK_ID = 9;     // DamageCodes

/**
 * Приводит признак повреждения к формату документа: все ссылки — { handbookId, id }.
 *
 * На вход принимает обе формы, которые встречаются в состоянии:
 * - из модалки Form4/Form5: { prichina, priznaki: [id] } или устаревшее { prizn },
 * - уже сохранённый в документе признак: { prichina, priznList: [...] }.
 *
 * Имя, код, sign_codes и прочее в документ не пишем: их резолвят по справочнику
 * и фронт (refToName / refToFullObject), и бэкенд (resolve_ref_name / resolve_ref_code).
 */
const normalizeDamageSign = (sign, handbooks = []) => {
  const priznSource = (() => {
    if (Array.isArray(sign.priznaki) && sign.priznaki.length > 0) return sign.priznaki;
    if (Array.isArray(sign.priznList) && sign.priznList.length > 0) return sign.priznList;
    if (Array.isArray(sign.prizn)) return sign.prizn;
    if (sign.prizn) return [sign.prizn];
    return [];
  })();

  return {
    prichina: sign.prichina ? toRef(sign.prichina, PRICHINA_HANDBOOK_ID, handbooks) : null,
    godpovr: sign.godpovr || '',
    dolaprizn: sign.dolaprizn !== undefined ? String(sign.dolaprizn) : '',
    specificDolaPrizn: sign.specificDolaPrizn !== undefined ? String(sign.specificDolaPrizn) : '',
    priznList: priznSource
      .map(p => toRef(p, PRIZN_HANDBOOK_ID, handbooks))
  };
};

/**
 * Главная порода — первая порода первого яруса. Берётся из уже собранного
 * plantationByTiers, где породы лежат готовыми ссылками { handbookId, id }.
 * Возвращает ссылку или null, если состав пуст.
 */
const mainSpeciesRef = (plantationByTiers = []) => {
  const tiers = Array.isArray(plantationByTiers) ? plantationByTiers : [];
  // Ярус «1», а если он не размечен — просто первый непустой по составу
  const firstTier =
    tiers.find(t => String(t?.yarushm ?? '') === '1' && (t?.sostav || []).length > 0) ||
    tiers.find(t => (t?.sostav || []).length > 0);
  const firstItem = firstTier?.sostav?.[0];
  const kodporoda = firstItem?.kodporoda;
  if (!kodporoda || typeof kodporoda !== 'object' || kodporoda.id === undefined || kodporoda.id === null) {
    return null;
  }
  return { handbookId: kodporoda.handbookId || 31, id: kodporoda.id };
};

/**
 * Преобразует данные Form2 в формат для сохранения
 */
const transformForm2 = (form2Data, independentForm2Data, form4Data, form5Data, handbooks = [], dataType = 'to', form1Data = {}) => {
  const distributionRow = (form, species) => {
    const rows = form?.data || [];
    const matches = row => String(row.yarus) === String(species.yarus) &&
      String(row.kodporoda) === String(species.kodporodato ?? species.kodporoda);
    return rows.find(row => String(row.id) === String(species.id) && matches(row)) ||
      rows.find(row => matches(row) && String(row.dolap) === String(species.dolap)) ||
      rows.find(matches);
  };
  // Преобразуем ярусы
  const plantationByTiers = independentForm2Data.yarusy.map(yarus => {
    const dependentYarus = independentForm2Data.yarusy_dependent.find(
      d => d.yarus === yarus.yarus || d.id === yarus.id
    ) || {};
    
    // Находим характеристики пород для этого яруса
    const porodaCharacteristics = independentForm2Data.poroda_characteristics
      .filter(p => p.yarus === yarus.yarus)
      .map(p => {
        const dependentPoroda = independentForm2Data.poroda_characteristics_dependent.find(
          d => d.id === p.id
        ) || {};
        
        // Получаем данные из Form4 (связываем по yarus и kodporoda)
        const form4Item = distributionRow(form4Data, p);
        
        // Получаем данные из Form5 (связываем по yarus и kodporoda)
        const form5Item = distributionRow(form5Data, p);
        
        // Формируем variant4 из Form4.
        // Если строки Form4 нет (компонент ни разу не монтировался — например,
        // у карточки выбрана форма 5), берём то, что пришло с сервера и лежит в
        // зависимых данных Form2. Иначе сохранение затирало бы нулями категории
        // санитарного состояния, которых пользователь даже не видел.
        const savedVariant4 = dependentPoroda.variant4 || {};
        const keepCat = (name) => String(savedVariant4[name] ?? '0');
        const variant4 = form4Item ? {
          catII: String(form4Item.osldolap || '0'),
          catIII: String(form4Item.sosldolap || '0'),
          catIV: String(form4Item.usdolap || '0'),
          catVa: String(form4Item.svsuhdolap || '0'),
          catVb: String(form4Item.svvtrdolap || '0'),
          catVc: String(form4Item.svburdolap || '0'),
          catVd: String(form4Item.stsuhdolap || '0'),
          catVe: String(form4Item.stvtrdolap || '0'),
          catVf: String(form4Item.stburdolap || '0')
        } : {
          catII: keepCat('catII'),
          catIII: keepCat('catIII'),
          catIV: keepCat('catIV'),
          catVa: keepCat('catVa'),
          catVb: keepCat('catVb'),
          catVc: keepCat('catVc'),
          catVd: keepCat('catVd'),
          catVe: keepCat('catVe'),
          catVf: keepCat('catVf')
        };
        
        // Формируем variant5 из Form5
        // Преобразуем causes (массив ID) в prichini (массив объектов причин)
        let prichini = [];
        if (form5Item?.causes && Array.isArray(form5Item.causes) && form5Item.causes.length > 0) {
          // Не удаляем пустые элементы: Variant5DistributionSchema МП считает
          // их ошибкой, и фронтенд должен показать то же самое перед отправкой.
          prichini = form5Item.causes.map(cause => toRef(cause, PRICHINA_HANDBOOK_ID, handbooks));
        }
        
        const variant5 = form5Item ? {
          cat25: String(form5Item.objedola25 || '0'),
          cat50: String(form5Item.objedola50 || '0'),
          cat75: String(form5Item.objedola75 || '0'),
          cat100: String(form5Item.objedola100 || '0'),
          prichini: prichini
        } : {
          cat25: '0',
          cat50: '0',
          cat75: '0',
          cat100: '0',
          prichini: []
        };
        
        // Получаем damageSigns из savedModalData (Form4 или Form5)
        // Ищем по id элемента из data (form4Item или form5Item)
        const itemId = form4Item?.id || form5Item?.id;
        const savedModalData = form4Data?.savedModalData || form5Data?.savedModalData || {};
        const modalData = itemId ? (savedModalData[itemId] || []) : [];
        
        const damageSigns = modalData.map(sign => normalizeDamageSign(sign, handbooks));
        
        // Преобразуем kodporoda в объект, если это ID
        // Справочник NamesOfBreeds имеет id=31
        let kodporodaObj = null;
        if (p.kodporoda || p.kodporodato) {
          const kodporodaValue = p.kodporoda || p.kodporodato;
          
          if (typeof kodporodaValue === 'object' && kodporodaValue?.id) {
            // Если уже объект с полными данными, используем его
            kodporodaObj = kodporodaValue;
          } else {
            // Пробуем найти по ID (если это число или строка-число)
            let kodporodaId = null;
            if (typeof kodporodaValue === 'number') {
              kodporodaId = kodporodaValue;
            } else if (typeof kodporodaValue === 'string') {
              // Пробуем распарсить как число
              const parsed = parseInt(kodporodaValue);
              if (!isNaN(parsed) && String(parsed) === kodporodaValue.trim()) {
                kodporodaId = parsed;
              }
            }
            
            if (kodporodaId !== null) {
              // Если это ID, получаем полные данные из справочника
              kodporodaObj = getHandbookReference(kodporodaId, 31, handbooks);
            }
            
            // Если не нашли по ID или это строка (название), ищем по названию
            if (!kodporodaObj && typeof kodporodaValue === 'string') {
              kodporodaObj = findReferenceByName(kodporodaValue, 31, handbooks);
            }
            
            // Если все еще не нашли, создаем базовый объект
            if (!kodporodaObj) {
              if (kodporodaId !== null) {
                kodporodaObj = { 
                  id: kodporodaId
                };
              } else {
                // Если это строка и не нашли, создаем объект с null ID
                kodporodaObj = { 
                  id: null
                };
              }
            }
          }
        }
        
        // Добавляем damageSigns в variant4, если есть данные из Form4.
        // Без строки Form4 — сохраняем признаки, пришедшие с сервера, по той же
        // причине, что и сами категории выше.
        // Признаки с сервера тоже прогоняем через нормализатор: карточка, сохранённая
        // без открытия ф.4, не должна консервировать старый формат ссылок.
        const savedDamageSigns = (dependentPoroda.damageSigns || [])
          .map(sign => normalizeDamageSign(sign, handbooks));
        const variant4WithDamageSigns = form4Item
          ? { ...variant4, damageSigns: damageSigns }
          : (savedDamageSigns.length > 0
              ? { ...variant4, damageSigns: savedDamageSigns }
              : variant4);
        
        return {
          dolap: p.dolap || '',
          // Единый формат справочной ссылки { handbookId, id } — тот же, что у
          // остальных полей и у мобильного приложения. Имя резолвится при чтении
          // (refToName на фронте, resolve_ref_name на бэкенде), дублировать его
          // в документе не нужно.
          kodporoda: toRef(kodporodaObj, 31, handbooks),
          // Поля называются так же, как в стейте и в UI, — берём значение как есть.
          vozr: String(dependentPoroda.vozr || p.vozr || ''),
          hsr: String(dependentPoroda.hsr || p.hsr || ''),
          dsr: String(dependentPoroda.dsr || p.dsr || ''),
          variant4: variant4WithDamageSigns,
          variant5: variant5
        };
      });
    
    // Преобразуем справочники для яруса
    // klvozr - AgeClasses (id=26) - исправлено с 27 на 26
    const klvozrValue = dependentYarus.klvozr || dependentYarus.klass_vozrasta;
    let klvozrObj = null;
    if (klvozrValue && klvozrValue !== '' && klvozrValue !== null) {
      klvozrObj = getHandbookReference(klvozrValue, 26, handbooks);
      // Если не нашли по ID, пробуем найти по названию
      if (!klvozrObj && typeof klvozrValue === 'string') {
        klvozrObj = findReferenceByName(klvozrValue, 26, handbooks);
      }
    }
    
    // grvozr - AgeGroups (id=27) - исправлено с 28 на 27
    const grvozrValue = dependentYarus.grvozr || dependentYarus.gruppa_vozrasta;
    let grvozrObj = null;
    if (grvozrValue && grvozrValue !== '' && grvozrValue !== null) {
      grvozrObj = getHandbookReference(grvozrValue, 27, handbooks);
      // Если не нашли по ID, пробуем найти по названию
      if (!grvozrObj && typeof grvozrValue === 'string') {
        grvozrObj = findReferenceByName(grvozrValue, 27, handbooks);
      }
    }
    
    return {
      yarushm: yarus.yarus || '',
      sostav: porodaCharacteristics,
      visota: String(dependentYarus.vysota || dependentYarus.visota || ''),
      klvozr: toRef(klvozrObj, 26, handbooks),
      grvozr: toRef(grvozrObj, 27, handbooks),
      polnota: String(dependentYarus.polnota || ''),
      zapassirga: String(dependentYarus.zapas_syro_ga || dependentYarus.zapassirga || '0'),
      zapassir: String(doubleOrNil(dependentYarus.zapas_syro_ga || dependentYarus.zapassirga) * mobilePlotArea(form1Data, dataType)),
      zapassuh: String(dependentYarus.zapas_suhostoy || dependentYarus.zapassuh || '0'),
      zapasredf: String(dependentYarus.zapas_redin || dependentYarus.zapasred || '0'),
      zapasedf: String(dependentYarus.zapas_ed_derev || dependentYarus.zapased || '0'),
      zapaszahl: String(dependentYarus.zapas_zah_obsh || dependentYarus.zapaszahl || '0'),
      zapaszahllikf: String(dependentYarus.zapas_zah_likvid || dependentYarus.zapaszahllik || '0')
    };
  });
  
  // Преобразуем справочники в объекты
  // cnl - PurposeForests (id=16)
  const cnlObj = form2Data.cnl ? getHandbookReference(form2Data.cnl, 16, handbooks) : null;
  
  // kzl - ProtectiveForestCategory (id=5)
  const kzlObj = form2Data.kzl ? getHandbookReference(form2Data.kzl, 5, handbooks) : null;
  
  // proishozdenie - OriginPlantings (id=24)
  const proishozdenieObj = form2Data.proishozdenie ? getHandbookReference(form2Data.proishozdenie, 24, handbooks) : null;
  
  // ozu - OZUType (id=1)
  const ozuObj = form2Data.ozu ? getHandbookReference(form2Data.ozu, 1, handbooks) : null;
  
  // arenda - ForestRights (id=11)
  const arendaObj = form2Data.arenda ? getHandbookReference(form2Data.arenda, 11, handbooks) : null;
  
  // vidarenda - AllowedUseType (id=4)
  const vidarendaObj = form2Data.vidarenda ? getHandbookReference(form2Data.vidarenda, 4, handbooks) : null;
  
  // oopt - OOPTType (id=3)
  const ooptObj = form2Data.oopt ? getHandbookReference(form2Data.oopt, 3, handbooks) : null;
  
  // katzem - ForestLand (id=6)
  const katzemObj = form2Data.katzem ? getHandbookReference(form2Data.katzem, 6, handbooks) : null;
  
  // gporoda - NamesOfBreeds (id=31). Главная порода не вводится руками: это
  // первая порода первого яруса. Считает её Form2, но полагаться только на неё
  // нельзя — вкладка может быть не открыта, а справочник пород не успеть
  // загрузиться, и тогда в документ ушёл бы null. Поэтому если в состоянии
  // пусто, берём породу прямо из уже собранного состава — это тот же источник,
  // но без промежуточной строки, которую надо разбирать обратно.
  const gporodaObj = form2Data.gporoda ? getHandbookReference(form2Data.gporoda, 31, handbooks) : null;
  const gporodaRef = toRef(gporodaObj, 31, handbooks) || mainSpeciesRef(plantationByTiers);

  // bonitet - DesignationBonitet (id=7)
  const bonitetObj = form2Data.bonitet ? getHandbookReference(form2Data.bonitet, 7, handbooks) : null;
  
  // tlu - TypesConditions (id=13)
  const tluObj = form2Data.tlu ? getHandbookReference(form2Data.tlu, 13, handbooks) : null;
  
  return {
    cnl: toRef(cnlObj, 16, handbooks),
    kzl: toRef(kzlObj, 5, handbooks),
    proishozdenie: toRef(proishozdenieObj, 24, handbooks),
    ozu: toRef(ozuObj, 1, handbooks),
    nozu: 'Нет',
    arenda: toRef(arendaObj, 11, handbooks),
    vidarenda: toRef(vidarendaObj, 4, handbooks),
    oopt: toRef(ooptObj, 3, handbooks),
    noopt: form2Data.noopt || '',
    glu: form2Data.glu || '',
    // Не выбрано — null, как в МП: иначе невыбранная доступность уходила в карточку как «Нет»
    landAvailability:
      form2Data.landAvailability === true || form2Data.landAvailability === 'true'
        ? true
        : (form2Data.landAvailability === false || form2Data.landAvailability === 'false' ? false : null),
    generalPlantationInfo: {
      katzem: toRef(katzemObj, 6, handbooks),
      gporoda: gporodaRef,
      bonitet: toRef(bonitetObj, 7, handbooks),
      tiplesa: form2Data.tiplesa || '',
      tlu: toRef(tluObj, 13, handbooks)
      // gigrotop/trofotop в карточку не пишем: это атрибуты выбранной записи
      // справочника 13, они однозначно выводятся из tlu и никем не читались.
    },
    plantationByTiers,
    useActualData: dataType === 'actual',
    itaks: form2Data.itaks2 || ''
  };
};

/**
 * Преобразует данные Form3 в формат для сохранения
 */
const transformForm3 = (form3Data, handbooks = []) => {
  
  // Извлекаем данные из массивов podlesok и podrost
  // В Form3 данные хранятся в массивах, берем первый элемент каждого массива
  const podlesokItem = (form3Data?.podlesok && form3Data.podlesok.length > 0) ? form3Data.podlesok[0] : {};
  const podrostItem = (form3Data?.podrost && form3Data.podrost.length > 0) ? form3Data.podrost[0] : {};
  
  // Функция для преобразования поля в объект справочника
  const getReferenceObject = (value, handbookId) => {
    if (!value) return null;
    
    // Если уже объект с id и name, возвращаем его
    if (typeof value === 'object' && value.id && value.name) {
      return value;
    }
    
    // Если это ID (число или строка-число), получаем из справочника
    const id = typeof value === 'string' ? parseInt(value) : value;
    if (!isNaN(id)) {
      return getHandbookReference(id, handbookId, handbooks);
    }
    
    return null;
  };
  
  // sostpod - ConditionUndergrowth (id=23) - из podrost
  const sostpodObj = getReferenceObject(podrostItem.sostpod, 23);
  
  // reljeff - ReliefForms (id=21) - из podlesok
  const reljeffObj = getReferenceObject(podlesokItem.reljeff, 21);
  
  // reljefeksp - ReliefExposures (id=17) - из podlesok
  const reljefekspObj = getReferenceObject(podlesokItem.reljefeksp, 17);
  
  // eroziavid - ErosionType (id=28) - из podlesok
  const eroziavidObj = getReferenceObject(podlesokItem.eroziavid, 28);
  
  // eroziastep - DegreesErosion (id=22) - из podlesok
  const eroziastepObj = getReferenceObject(podlesokItem.eroziastep, 22);
  
  // provlhm - EconomicMeasure (id=32) - из podlesok
  const provlhmObj = getReferenceObject(podlesokItem.provlhm, 32);
  
  return {
    // Данные из podrost
    sostavpod: podrostItem.sostavpod || '',
    vozrpod: podrostItem.vozrpod || '',
    hpod: podrostItem.hpod || '',
    gustpod: podrostItem.gustpod || '',
    sostpod: toRef(sostpodObj, 23, handbooks),
    // Данные из podlesok
    sostavpdl: podlesokItem.sostavpdl || '',
    gustpdl: podlesokItem.gustpdl || '',
    hpdl: podlesokItem.hpdl || '',
    sostpdl: podlesokItem.sostpdl || '',
    reljeff: toRef(reljeffObj, 21, handbooks),
    reljefeksp: toRef(reljefekspObj, 17, handbooks),
    reljefuklon: podlesokItem.reljefuklon || '',
    eroziavid: toRef(eroziavidObj, 28, handbooks),
    eroziastep: toRef(eroziastepObj, 22, handbooks),
    provlhm: toRef(provlhmObj, 32, handbooks),
    itaks: podlesokItem.itaks3 || podlesokItem.itaks || form3Data?.itaks3 || form3Data?.itaks || ''
  };
};

/**
 * Преобразует данные Form4 в формат для сохранения
 */
const transformForm4 = (form4Data, handbooks = []) => {
  // charochag - NatureShrinkage (id=20, используется в Form4)
  const charochagObj = form4Data.charochag ? getHandbookReference(form4Data.charochag, 20, handbooks) : null;
  
  return {
    charochag: toRef(charochagObj, 20, handbooks),
    itaks: form4Data.itaks4 || ''
  };
};

/**
 * Преобразует данные Form5 в формат для сохранения
 */
const transformForm5 = (form5Data, handbooks = []) => {
  // Преобразуем ochagPrichina из ID в объект справочника (справочник CausesOfDamage имеет id=10)
  const prichinaObj = form5Data.ochagPrichina ? getHandbookReference(form5Data.ochagPrichina, 10, handbooks) : null;
  
  return {
    harmfulOrganisms: form5Data.harmfulOrganisms || [],
    itaks: form5Data.itaks5 || '',
    prichina: toRef(prichinaObj, 10, handbooks), // Сохраняем причину в формате { handbookId, id }
    ochagPrichina: form5Data.ochagPrichina || '', // Сохраняем также ID для обратной совместимости
    ochagVidVO: form5Data.ochagVidVO || '',
    ochagFaza: form5Data.ochagFaza || '',
    ochagTip: form5Data.ochagTip || ''
  };
};

/** Фаза очага в формате сервера: старые значения формы (arising/acting) — в коды МП. */
const ochagPhaseForSave = (phase) => {
  if (phase === 'arising') return 'EMERGING';
  if (phase === 'acting') return 'ACTIVE';
  return typeof phase === 'string' && phase.length > 0 ? phase : null;
};

/** Тип очага в формате сервера: chronic/episodic → CHRONIC/EPISODIC. */
const ochagTypeForSave = (type) => {
  if (type === 'chronic') return 'CHRONIC';
  if (type === 'episodic') return 'EPISODIC';
  return typeof type === 'string' && type.length > 0 ? type : null;
};

/**
 * Преобразует данные Form6 в формат для сохранения
 */
const transformForm6 = (form6Data, handbooks = [], form5Data = null, form4Data = null) => {
  
  // Функция для преобразования поля в объект справочника
  const getReferenceObject = (value, handbookId) => {
    if (!value) return null;
    
    // Если уже объект с id и name, возвращаем его
    if (typeof value === 'object' && value.id && value.name) {
      return value;
    }
    
    // Если это ID (число или строка-число), получаем из справочника
    const id = typeof value === 'string' ? parseInt(value) : value;
    if (!isNaN(id)) {
      return getHandbookReference(id, handbookId, handbooks);
    }
    
    return null;
  };
  
  const recommendedActivities = (form6Data?.recommended_measures || []).map(measure => {
    // rekvidmer - Вид мероприятия (справочник id=25)
    const rekvidmerObj = getReferenceObject(measure.rekvidmer, 25);
    
    return {
      rekvidmer: toRef(rekvidmerObj, 25, handbooks),
      srokmzl: measure.srokmzl || '',
      srekmer: measure.srekme || '', // Преобразуем srekme в srekmer
      primechmzl: measure.primechmzl || ''
    };
  });
  
  // Собираем данные об очагах из form4Data (приоритет) или form5Data
  const selectedOchagCodes = [];
  const vidvo = {};
  // Код главного очага: в карточке он стоит первым — так главный очаг ищут МП и отчёты
  let mainOchagCode = null;
  
  // Если есть данные очагов в Form4, используем их
  if (form4Data && form4Data.selectedOchags && form4Data.ochagData) {
    const selectedOchags = form4Data.selectedOchags || [];
    const ochagData = form4Data.ochagData || {};
    
    // Получаем справочник причин (ID 10) для получения кодов
    const causesHandbook = handbooks.find(h => h.id === 10);
    // Получаем справочник видов ВО (ID 40) для получения данных ВО
    const pestSpeciesHandbook = handbooks.find(h => h.id === 40);
    
    if (causesHandbook && causesHandbook.references && pestSpeciesHandbook && pestSpeciesHandbook.references) {
      // Находим главный очаг (должен быть только один)
      // Ищем по causeKey и по коду причины
      let mainOchagKey = null;
      let mainOchagCauseId = null;
      selectedOchags.forEach((causeKey) => {
        // Ищем данные по causeKey
        let ochagInfo = ochagData[causeKey];
        // Если не нашли, ищем по коду причины
        if (!ochagInfo) {
          const causeId = causeKey.split('-')[0];
          const cause = causesHandbook.references.find(
            r => r.id.toString() === causeId
          );
          if (cause && cause.code) {
            ochagInfo = ochagData[String(cause.code)];
          }
        }
        
        if (ochagInfo && ochagInfo.isMain) {
          if (!mainOchagKey) {
            mainOchagKey = causeKey;
            const causeId = causeKey.split('-')[0];
            mainOchagCauseId = causeId;
          }
        }
      });
      
      selectedOchags.forEach((causeKey) => {
        // causeKey имеет формат "id-index", извлекаем ID причины
        const causeId = causeKey.split('-')[0];
        
        // Находим причину в справочнике по ID
        const cause = causesHandbook.references.find(
          r => r.id.toString() === String(causeId)
        );
        
        if (!cause || !cause.code) {
          return;
        }
        
        const causeCode = cause.code;
        selectedOchagCodes.push(causeCode);
        
        // Получаем данные очага для этой причины
        // Сначала ищем по causeKey, затем по коду причины
        let ochagInfo = ochagData[causeKey];
        if (!ochagInfo && cause.code) {
          ochagInfo = ochagData[String(cause.code)];
        }
        if (!ochagInfo) {
          return;
        }
        
        // Получаем данные вида ВО, если указан vo
        let vidvoObj = null;
        if (ochagInfo.vo) {
          const voId = typeof ochagInfo.vo === 'string' ? parseInt(ochagInfo.vo) : ochagInfo.vo;
          const voRef = pestSpeciesHandbook.references.find(
            r => r.id.toString() === String(voId)
          );
          
          if (voRef) {
            vidvoObj = {
              id: voRef.id,
              name: voRef.name || '',
              code: voRef.code || null,
              description: voRef.description || null,
              group: voRef.group || null,
              disease_type: voRef.disease_type || null,
              views_codes: voRef.views_codes || [],
              ...Object.fromEntries(
                Object.entries(voRef).filter(([key]) => 
                  !['id', 'name', 'code', 'description', 'group', 'disease_type', 'views_codes'].includes(key)
                )
              )
            };
            
            // Добавляем handbookId, если есть
            if (pestSpeciesHandbook.id) {
              vidvoObj.handbookId = pestSpeciesHandbook.id;
            }
          }
        }
        
        // Преобразуем фазу и тип очага из формата Form4 в формат для сохранения
        // Только для главного очага сохраняем фазу и тип
        let ochagPhasa = null;
        let ochagType = null;
        
        // Сохраняем фазу и тип только если это главный очаг
        // Проверяем по causeKey или по ID причины
        const isMainOchag = (causeKey === mainOchagKey) || 
          (mainOchagCauseId && causeId === mainOchagCauseId && ochagInfo.isMain);
        
        if (isMainOchag && ochagInfo.isMain) {
          if (ochagInfo.phase) {
            // Преобразуем фазу из формата Form4 в формат для сохранения
            // Поддерживаем как старые значения формы (arising, acting), так и коды сервера
            // (INITIAL, EMERGING, ACTIVE и т.д.). «acting» — это ACTIVE: значения ACTING в МП нет.
            ochagPhasa = ochagPhaseForSave(ochagInfo.phase);
          }
          
          if (ochagInfo.type) {
            // Преобразуем тип из формата Form4 в формат для сохранения
            // Поддерживаем как новые значения (chronic, episodic), так и старые (CHRONIC, EPISODIC)
            if (ochagInfo.type === 'chronic') {
              ochagType = 'CHRONIC';
            } else if (ochagInfo.type === 'episodic') {
              ochagType = 'EPISODIC';
            } else if (typeof ochagInfo.type === 'string' && ochagInfo.type.length > 0) {
              // Если тип уже в формате сервера (CHRONIC, EPISODIC), используем как есть
              ochagType = ochagInfo.type;
            }
          }
        }
        
        if (isMainOchag && ochagInfo.isMain && mainOchagCode === null) {
          mainOchagCode = causeCode;
        }

        // Сохраняем данные очага по коду причины
        vidvo[String(causeCode)] = {
          vidvo: vidvoObj,
          ochagPhasa: ochagPhasa,
          ochagType: ochagType
        };
      });
    }
  }
  // Если данных из Form4 нет, используем данные из Form5 (новый формат с selectedOchags и ochagData)
  else if (form5Data && form5Data.selectedOchags && form5Data.ochagData) {
    const selectedOchags = form5Data.selectedOchags || [];
    const ochagData = form5Data.ochagData || {};
    
    // Получаем справочник причин (ID 10) для получения кодов
    const causesHandbook = handbooks.find(h => h.id === 10);
    // Получаем справочник видов ВО (ID 40) для получения данных ВО
    const pestSpeciesHandbook = handbooks.find(h => h.id === 40);
    
    if (causesHandbook && causesHandbook.references && pestSpeciesHandbook && pestSpeciesHandbook.references) {
      selectedOchags.forEach((causeKey) => {
        // В Form5 ключ - это ID причины (String(cause.id))
        const causeId = String(causeKey);
        
        // Находим причину в справочнике по ID
        const cause = causesHandbook.references.find(
          r => r.id.toString() === causeId
        );
        
        if (!cause || !cause.code) {
          return;
        }
        
        const causeCode = cause.code;
        selectedOchagCodes.push(causeCode);
        
        // Получаем данные очага для этой причины
        // Сначала ищем по causeKey (ID причины), затем по коду причины
        let ochagInfo = ochagData[causeKey];
        if (!ochagInfo && cause.code) {
          ochagInfo = ochagData[String(cause.code)];
        }
        if (!ochagInfo) {
          return;
        }
        
        // Получаем данные вида ВО, если указан vo
        let vidvoObj = null;
        if (ochagInfo.vo) {
          const voId = typeof ochagInfo.vo === 'string' 
            ? parseInt(ochagInfo.vo) 
            : ochagInfo.vo;
          
          const vo = pestSpeciesHandbook.references.find(
            r => r.id.toString() === String(voId)
          );
          
          if (vo) {
            vidvoObj = {
              id: vo.id,
              name: vo.name || '',
              code: vo.code || null,
              description: vo.description || null,
              group: vo.group || null,
              disease_type: vo.disease_type || null,
              views_codes: vo.views_codes || [],
              ...Object.fromEntries(
                Object.entries(vo).filter(([key]) => 
                  !['id', 'name', 'code', 'description', 'group', 'disease_type', 'views_codes'].includes(key)
                )
              )
            };
            
            if (pestSpeciesHandbook.id) {
              vidvoObj.handbookId = pestSpeciesHandbook.id;
            }
          }
        }
        
        // Фаза и тип — только у главного очага, как в форме №4. Раньше здесь они
        // терялись, и главный очаг формы №5 уходил на сервер и в МП без них.
        const isMainOchag = !!ochagInfo.isMain && mainOchagCode === null;
        if (isMainOchag) {
          mainOchagCode = causeCode;
        }

        // Сохраняем данные очага по коду причины
        vidvo[String(causeCode)] = {
          vidvo: vidvoObj,
          ochagPhasa: isMainOchag ? ochagPhaseForSave(ochagInfo.phase) : null,
          ochagType: isMainOchag ? ochagTypeForSave(ochagInfo.type) : null
        };
      });
    }
  }
  // Поддержка старого формата для обратной совместимости
  else if (form5Data && form5Data.ochagPrichina) {
    // Получаем справочник причин (id=10) для получения кода причины
    const causesHandbook = handbooks.find(h => h.id === 10);
    
    if (causesHandbook && causesHandbook.references) {
      // Получаем ID причины
      const prichinaId = typeof form5Data.ochagPrichina === 'string' 
        ? parseInt(form5Data.ochagPrichina) 
        : form5Data.ochagPrichina;
      
      // Находим запись в справочнике причин
      const prichinaRef = causesHandbook.references.find(
        r => r.id.toString() === String(prichinaId)
      );
      
      if (prichinaRef && prichinaRef.code) {
        const prichinaCode = prichinaRef.code;
        
        // Получаем справочник видов ВО (id=40) для получения данных ВО, если указан ochagVidVO
        const pestSpeciesHandbook = handbooks.find(h => h.id === 40);
        let vidvoObj = null;
        
        if (form5Data.ochagVidVO && pestSpeciesHandbook && pestSpeciesHandbook.references) {
          // Получаем ID вида ВО
          const vidVOId = typeof form5Data.ochagVidVO === 'string' 
            ? parseInt(form5Data.ochagVidVO) 
            : form5Data.ochagVidVO;
          
          // Находим запись в справочнике видов ВО
          const vidVORef = pestSpeciesHandbook.references.find(
            r => r.id.toString() === String(vidVOId)
          );
          
          if (vidVORef) {
            // Формируем полный объект вида ВО
            vidvoObj = {
              id: vidVORef.id,
              name: vidVORef.name || '',
              code: vidVORef.code || null,
              description: vidVORef.description || null,
              group: vidVORef.group || null,
              disease_type: vidVORef.disease_type || null,
              views_codes: vidVORef.views_codes || [],
              ...Object.fromEntries(
                Object.entries(vidVORef).filter(([key]) => 
                  !['id', 'name', 'code', 'description', 'group', 'disease_type', 'views_codes'].includes(key)
                )
              )
            };
            
            // Добавляем handbookId, если есть
            if (pestSpeciesHandbook.id) {
              vidvoObj.handbookId = pestSpeciesHandbook.id;
            }
          }
        }
        
        // Преобразуем фазу и тип очага
        // ochagFaza -> ochagPhasa (INITIAL, OUTBREAK, EMERGING, etc.)
        // ochagTip -> ochagType (EPISODIC, CHRONIC, etc.)
        let ochagPhasa = null;
        if (form5Data.ochagFaza) {
          const phasaMapping = {
            'начальная': 'INITIAL',
            'нарастания численности': 'POPULATION_GROWTH',
            'собственно вспышка': 'OUTBREAK',
            'кризис': 'CRISIS',
            'возникающий': 'EMERGING',
            'действующий': 'ACTIVE',
            'затухающий': 'FADING'
          };
          if (phasaMapping[form5Data.ochagFaza]) {
            ochagPhasa = phasaMapping[form5Data.ochagFaza];
          } else {
            // Если уже в английском формате, используем как есть
            ochagPhasa = form5Data.ochagFaza;
          }
        }
        
        // Преобразуем из формата формы (эпизодический/хронический) в формат сервера (EPISODIC/CHRONIC)
        let ochagType = null;
        if (form5Data.ochagTip) {
          if (form5Data.ochagTip === 'эпизодический') {
            ochagType = 'EPISODIC';
          } else if (form5Data.ochagTip === 'хронический') {
            ochagType = 'CHRONIC';
          } else {
            // Если уже в английском формате, используем как есть
            ochagType = form5Data.ochagTip;
          }
        }
        
        // Сохраняем данные очага по коду причины
        vidvo[String(prichinaCode)] = {
          vidvo: vidvoObj,
          ochagPhasa: ochagPhasa,
          ochagType: ochagType
        };
        
        // Добавляем код в selectedOchagCodes
        selectedOchagCodes.push(prichinaCode);
      }
    }
  }
  
  // Пока вкладка очагов не открыта, её локального состояния ещё нет. Прямой
  // выбор из сохранённой формы №6 нельзя терять, особенно если vidvo пусто:
  // отсутствие данных выбранного главного очага должно оставаться ошибкой.
  const hasLocalSelection = Array.isArray(form4Data?.selectedOchags) || Array.isArray(form5Data?.selectedOchags);
  if (!hasLocalSelection && Array.isArray(form6Data?.selectedOchagCodes)) {
    selectedOchagCodes.splice(0, selectedOchagCodes.length, ...form6Data.selectedOchagCodes);
    mainOchagCode = null;
    Object.keys(vidvo).forEach(key => delete vidvo[key]);
    Object.entries(form6Data.vidvo || {}).forEach(([code, entry]) => {
      vidvo[code] = {
        vidvo: toRef(entry?.vidvo, 40, handbooks),
        ochagPhasa: entry?.ochagPhasa ?? null,
        ochagType: entry?.ochagType ?? null,
      };
    });
  }

  return {
    recommendedActivities,
    // Наличие рекомендованных мероприятий: YES / NO / NONE (см. utils/recommendedMeasures)
    activities_required: activitiesRequiredForSave(form6Data),
    selectedOchagCodes: mainOchagCode === null
      ? selectedOchagCodes
      : [mainOchagCode, ...selectedOchagCodes.filter(code => code !== mainOchagCode)],
    vidvo,
    itaks: form6Data?.itaks6 || form6Data?.itaks || ''
  };
};

/**
 * Валидация всех обязательных полей
 * @param {object} formsData
 * @param {object} independentForm2Data
 * @param {Array} [handbooks] — справочники: с ними карточка проверяется правилами МП (cardValidation)
 * @param {string} [dataType] — 'to' | 'actual' (какой набор формы №2 уходит в карточку)
 */
// Набор формы №2 текущего режима: «материалы ТО» и «фактические данные» хранятся
// раздельно, а в карточку уходит тот, с которым человек работает. Запасные
// варианты — для старых карточек, где второго набора ещё нет.
const form2ForDataType = (form2, dataType) =>
  (form2 && (form2[dataType] || form2.to || form2.actual)) || {};

export const validateAllForms = (formsData, independentForm2Data, handbooks = [], dataType = 'to', selectedForm45 = null) => {
  // Справочники загружены — проверяем карточку в том виде, в каком она уйдёт на
  // сервер, теми же правилами, что сервер и мобильное приложение (cardValidation):
  // панель «Замечания» показывает ровно то, из-за чего карточку не примут.
  if (Array.isArray(handbooks) && handbooks.length > 0) {
    let card = null;
    try {
      card = transformFormsData(formsData, independentForm2Data, selectedForm45, handbooks, dataType);
    } catch (e) {
      card = null;
    }
    if (card) {
      const errors = validateCardRules(card, handbooks).map(({ form, message }) => `${form}: ${message}`);
      return { isValid: errors.length === 0, errors };
    }
  }
  // Справочники ещё не пришли или данные формы не собраны — основные обязательные
  // поля по состоянию экрана, по тем же правилам МП.
  const errors = validateFromScreenState(formsData, independentForm2Data, handbooks, dataType);
  return { isValid: errors.length === 0, errors };
};

const validateFromScreenState = (formsData, independentForm2Data, handbooks, dataType) => {
  const errors = [];

  const form1 = formsData.form1 || {};
  const form1Required = [
    { field: 'srf', name: 'Субъект РФ' },
    { field: 'filial', name: 'Организация, проводившая работу' },
    { field: 'lvo', name: 'Лесничество' },
    { field: 'kv', name: 'Квартал' },
    { field: 'vid', name: '№ выдела' },
    { field: 'sga', name: 'S, га' },
    { field: 'slp', name: 'S лесопатологического выдела, га' },
    { field: 'cel', name: 'Цель ВНН' },
    // Дата проведения ВНН — это datar; в data лежит дата создания карточки,
    // она проставляется сама при сохранении. Пустой № лесопатологического выдела
    // форма показывает и сохраняет как «1».
    { field: 'datar', name: 'Дата проведения ВНН' }
  ];
  form1Required.forEach(({ field, name }) => {
    if (!form1[field] || form1[field] === '') {
      errors.push(`Form1: ${name} не заполнено`);
    }
  });

  const form2 = form2ForDataType(formsData.form2, dataType);
  [
    { field: 'cnl', name: 'Целевое назначение лесов' },
    { field: 'glu', name: 'Год лесоустройства' },
    { field: 'katzem', name: 'Категория земель' }
  ].forEach(({ field, name }) => {
    if (!form2[field] || form2[field] === '') {
      errors.push(`Form2: ${name} не заполнено`);
    }
  });

  // П. 2.8: «Категория защитных лесов» обязательна при целевом назначении «Защитные леса»
  if (isProtectiveForestsCnl(form2.cnl, handbooks) && (!form2.kzl || form2.kzl === '')) {
    errors.push('Form2: Категория защитных лесов не заполнено');
  }

  const la = form2.landAvailability;
  if (la !== true && la !== false) {
    errors.push('Form2: Доступность участка не заполнено');
  }

  // Как в МП: бонитет и ярусы обязательны на землях, покрытых лесом (запись 14
  // справочника категорий земель), а уже введённые ярусы проверяются на любой земле.
  const covered = refToId(form2.katzem) === '14';
  if (covered && (!form2.bonitet || form2.bonitet === '')) {
    errors.push('Form2: Бонитет не заполнено');
  }
  const yarusy = independentForm2Data?.yarusy || [];
  if (covered && yarusy.length === 0) {
    errors.push('Form2: Не заполнены ярусы');
  }
  yarusy.forEach((yarus, index) => {
    if (!yarus.sostav || yarus.sostav === '') {
      errors.push(`Form2: Ярус ${yarus.yarus || index + 1} — не указан состав`);
    }
  });
  (independentForm2Data?.yarusy_dependent || []).forEach((yarus, index) => {
    const valueRaw = String(yarus?.zapassirga ?? '').trim();
    const value = parseFloat(valueRaw.replace(',', '.'));
    const yarusNumber = yarus?.yarus || index + 1;
    if (!valueRaw) {
      errors.push(`Form2: Ярус ${yarusNumber} — не заполнено поле "Запас сырораст., кбм на га"`);
    } else if (!Number.isNaN(value) && value < 0.1) {
      errors.push(`Form2: Ярус ${yarusNumber} — запас сырорастущего должен быть не меньше 0,1 кбм на га`);
    }
  });

  return errors;
};

/**
 * Преобразует все данные форм в формат для сохранения
 */
export const transformFormsData = (formsData, independentForm2Data, selectedForm45, handbooks = [], dataType = 'to') => {
  const form2Data = form2ForDataType(formsData.form2, dataType);
  return {
    optionalTab: selectedForm45 === 4 ? 'FORM_4' : 'FORM_5',
    form1: transformForm1(formsData.form1, handbooks),
    form2: transformForm2(form2Data, independentForm2Data, formsData.form4, formsData.form5, handbooks, dataType, formsData.form1),
    form3: transformForm3(formsData.form3, handbooks),
    form4: transformForm4(formsData.form4, handbooks),
    form5: transformForm5(formsData.form5, handbooks),
    form6: transformForm6(formsData.form6, handbooks,
      selectedForm45 === 5 ? formsData.form5 : null,
      selectedForm45 === 4 ? formsData.form4 : null)
  };
};

/**
 * Создаёт документ для сохранения.
 * API v2: id документа больше не генерируется на клиенте — его всегда назначает
 * сервер при создании (см. saveDocument/POST /v2/reports/create/). До первого
 * сохранения у документа просто нет id.
 */
export const createDocument = (formsData, independentForm2Data, selectedForm45, userId, handbooks = [], documentName = null, dataType = 'to') => {
  const today = getTodayDate();
  const name = documentName || `Документ от ${today}`;
  const transformedData = transformFormsData(formsData, independentForm2Data, selectedForm45, handbooks, dataType);

  return {
    name,
    ownerId: userId,
    data: { ...transformedData }
  };
};

/**
 * Отправляет документ на сервер через API v2.
 * POST /v2/reports/create/ (новый документ) или PUT /v2/reports/{docId}/ (обновление
 * существующего — reportId должен существовать, иначе сервер вернёт 404, а не тихо
 * создаст новую запись под этим id, как было в старом API).
 * @param {Object} document - Документ для сохранения (name, data — форма/контент)
 * @param {string} [reportId] - id отчёта на сервере (для обновления существующего)
 * @param {*} [_docversionFromOpened] - не используется в API v2 (modifiedAt считает сервер); оставлен для совместимости сигнатуры вызова
 * @param {number} [versionFromOpened] - версия документа, известная клиенту на момент открытия (опциональная проверка конфликта версий на сервере)
 */
export const saveDocument = async (document, reportId = null, _docversionFromOpened = null, versionFromOpened = null) => {
  const token = getAccessToken();
  if (!token) {
    throw new Error('Необходима авторизация');
  }

  const name = document.name;
  const nestedData = document.data || {}; // optionalTab, form1, form2, form3, form4, form5, form6
  const cardType = nestedData.type || document.type || 'VNN';

  const properties = {
    name: name || `Документ от ${new Date().toLocaleDateString('ru-RU')}`,
  };
  if (!reportId) {
    // properties.type/handbookVersion фиксируются только при создании — при
    // обновлении редактируется только name (см. описание API v2).
    properties.type = cardType;
  }
  if (versionFromOpened !== null && versionFromOpened !== undefined) {
    properties.version = versionFromOpened;
  }

  const requestBody = { properties, content: nestedData };

  const makeRequest = async (headers) => {
    const url = reportId
      ? `${API_V2_BASE}/reports/${reportId}/`
      : `${API_V2_BASE}/reports/create/`;
    const method = reportId ? 'PUT' : 'POST';

    return await fetch(url, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        ...headers
      },
      body: JSON.stringify(requestBody)
    });
  };

  try {
    // Первая попытка с текущим токеном
    let response = await makeRequest(getAuthHeaders());

    // Если получили 401, пытаемся обновить токен
    if (response.status === 401) {
      const refreshResult = await refreshAccessToken();

      if (refreshResult.success) {
        // Повторяем запрос с новым токеном
        response = await makeRequest(getAuthHeaders());
      } else {
        throw new Error('Токен истек. Необходимо войти в систему заново.');
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      let errorMessage = `Ошибка при сохранении документа (${response.status})`;
      if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.detail) {
        errorMessage = errorData.detail;
      }
      const err = new Error(errorMessage);
      err.status = response.status;
      err.payload = errorData;
      throw err;
    }

    const savedDocument = await response.json();
    // API v2: { document: { properties: { id, name, version, type, createdAt, modifiedAt, executor, approvalState }, content: {...} } }
    const savedProperties = savedDocument?.document?.properties || {};
    const newReportId = savedProperties.id;

    if (!newReportId) {
      throw new Error('Сервер не вернул id документа');
    }

    return {
      success: true,
      reportId: newReportId,
      version: savedProperties.version,
      name: savedProperties.name,
      modifiedAt: savedProperties.modifiedAt,
      approvalState: savedProperties.approvalState,
      canEdit: savedProperties.canEdit,
    };
  } catch (error) {
    return { success: false, error: error.message || 'Неизвестная ошибка при сохранении', status: error.status };
  }
};

/**
 * Преобразует данные из формата сервера в формат форм для загрузки
 */
export const loadDocumentDataFromServer = (serverData, handbooks = []) => {
  
  // Определяем структуру данных
  let documentData;
  if (serverData.data) {
    // Если есть поле data, проверяем, есть ли в нём вложенное data
    const topLevelData = serverData.data;
    
    if (topLevelData.data && typeof topLevelData.data === 'object' && !Array.isArray(topLevelData.data) && topLevelData.data.form1 !== undefined) {
      // Есть двойное вложение: data.data (формы находятся внутри data.data)
      documentData = topLevelData.data;
    } else if (topLevelData.form1 !== undefined || topLevelData.optionalTab !== undefined) {
      // Формы находятся в data напрямую
      documentData = topLevelData;
    } else {
      // Пробуем использовать data.data, если оно есть
      documentData = topLevelData.data || topLevelData;
    }
  } else {
    // Если нет поля data, возможно данные пришли напрямую
    documentData = serverData;
  }
  
  // Извлекаем данные форм
  const form1Data = documentData.form1 || {};
  const form2Data = documentData.form2 || {};
  const form3Data = documentData.form3 || {};
  const form4Data = documentData.form4 || {};
  const form5Data = documentData.form5 || {};
  const form6Data = documentData.form6 || {};
  const optionalTab = documentData.optionalTab || 'FORM_4';
  
  // Преобразуем form2 - нужно разделить на to/actual и independentForm2Data
  // form2 из сервера содержит plantationByTiers, generalPlantationInfo и т.д.
  // Нужно преобразовать в формат, который ожидают формы
  
  // Для form2 нужно извлечь данные из plantationByTiers
  const plantationByTiers = form2Data.plantationByTiers || [];
  
  // Преобразуем ярусы и характеристики пород
  // Функция для преобразования массива sostav в строку состава
  const buildCompositionString = (sostavArray) => {
    if (!sostavArray || !Array.isArray(sostavArray) || sostavArray.length === 0) {
      return '';
    }
    
    return sostavArray
      .filter(item => item.dolap && item.kodporoda)
      .map(item => {
        // Получаем название породы из kodporoda. Формат ссылки зависит от клиента:
        // веб сохраняет { id, name, description }, мобильное приложение —
        // нормализованное { handbookId, id } без name. Во втором случае имя
        // разрешаем по справочнику: без него в строке состава остаются одни доли
        // («73» вместо «7Аа3Аб»), а Form2 потом не может её разобрать обратно
        // и очищает таблицу характеристик пород.
        // refToName понимает все форматы ссылки: { handbookId, id } (так пишут
        // и мобильное приложение, и теперь веб), старый полный объект
        // { id, name, ... } и строку.
        const kodporoda = item.kodporoda;
        let porodaName = refToName(kodporoda, handbooks);
        // Голый id refToName вернёт как есть — разрешаем его по справочнику пород,
        // иначе в строку состава попадёт число вместо букв.
        if (/^\d+$/.test(String(porodaName).trim())) {
          porodaName = refToName({ handbookId: 31, id: String(porodaName).trim() }, handbooks) || porodaName;
        }

        // Форматируем название породы: первая буква заглавная, остальные строчные
        const formattedPoroda = porodaName ? (porodaName.charAt(0).toUpperCase() + porodaName.slice(1).toLowerCase()) : '';
        
        return `${item.dolap}${formattedPoroda}`;
      })
      .join('');
  };
  
  // Выбранные породы яруса (ID записей справочника) — в том же порядке и с тем
  // же отбором, что и строка состава: по ним Form2 строит характеристики пород,
  // а не по буквам строки, за которыми может стоять несколько пород. Если у
  // какой-то породы нет числового ID (очень старый формат — одно название),
  // списка нет, и Form2 разберёт строку как раньше.
  const sostavItemsOf = (sostavArray) => {
    const items = (Array.isArray(sostavArray) ? sostavArray : [])
      .filter(item => item.dolap && item.kodporoda)
      .map(item => ({
        dolap: String(item.dolap),
        kodporodato: String(typeof item.kodporoda === 'object' ? item.kodporoda.id ?? '' : item.kodporoda).trim()
      }));
    return items.every(item => /^\d+$/.test(item.kodporodato)) ? items : undefined;
  };

  const yarusy = plantationByTiers.map((tier, index) => ({
    id: index + 1,
    yarus: tier.yarushm || String(index + 1),
    sostav: buildCompositionString(tier.sostav), // Преобразуем массив sostav в строку
    sostavItems: sostavItemsOf(tier.sostav)
  }));
  
  const yarusyDependent = plantationByTiers.map((tier, index) => {
    // Преобразуем klvozr и grvozr из объектов в ID (строки)
    const klvozrValue = tier.klvozr;
    const klvozrId = typeof klvozrValue === 'object' && klvozrValue?.id 
      ? String(klvozrValue.id) 
      : (klvozrValue ? String(klvozrValue) : null);
    
    const grvozrValue = tier.grvozr;
    const grvozrId = typeof grvozrValue === 'object' && grvozrValue?.id 
      ? String(grvozrValue.id) 
      : (grvozrValue ? String(grvozrValue) : null);
    
    return {
      id: index + 1,
      yarus: tier.yarushm || String(index + 1),
      visota: tier.visota || '', // Используем visota вместо vysota для совместимости с Form2
      klvozr: klvozrId,
      grvozr: grvozrId,
      polnota: tier.polnota || '',
      zapassirga: tier.zapassirga || '',
      zapassir: tier.zapassir || '',
      zapassuh: tier.zapassuh || '',
      zapasred: tier.zapasredf || tier.zapasred || '', // Преобразуем zapasredf в zapasred
      zapased: tier.zapasedf || tier.zapased || '', // Преобразуем zapasedf в zapased
      zapaszahl: tier.zapaszahl || '',
      zapaszahllik: tier.zapaszahllikf || tier.zapaszahllik || '' // Преобразуем zapaszahllikf в zapaszahllik
    };
  });
  
  // Преобразуем характеристики пород из sostav
  const porodaCharacteristics = [];
  const porodaCharacteristicsDependent = [];
  
  plantationByTiers.forEach((tier, tierIndex) => {
    const sostav = tier.sostav || [];
    sostav.forEach((item, itemIndex) => {
      const porodaId = tierIndex * 1000 + itemIndex + 1;
      // Преобразуем kodporoda из объекта в ID (строку) или оставляем как есть
      const kodporodaValue = item.kodporoda;
      const kodporodaId = typeof kodporodaValue === 'object' && kodporodaValue?.id 
        ? String(kodporodaValue.id) 
        : (kodporodaValue ? String(kodporodaValue) : null);
      
      porodaCharacteristics.push({
        id: porodaId,
        yarus: tier.yarushm || String(tierIndex + 1),
        // yarusId должен совпадать с id яруса из yarusy (там это index + 1):
        // по нему Form2 при пересборке таблицы опознаёт уже существующие строки
        // и сохраняет их id, а вместе с ним — введённые ср. возраст/высоту/диаметр.
        yarusId: tierIndex + 1,
        kodporodato: kodporodaId,
        kodporoda: kodporodaId,
        dolap: item.dolap || '' // Добавляем dolap также в porodaCharacteristics для совместимости
      });
      
      porodaCharacteristicsDependent.push({
        id: porodaId,
        yarus: tier.yarushm || String(tierIndex + 1),
        dolap: item.dolap || '',
        kodporodato: kodporodaId, // Добавляем kodporodato для Form5
        vozr: item.vozr || '',
        hsr: item.hsr || '',
        dsr: item.dsr || '',
        variant4: item.variant4 || {},
        variant5: item.variant5 || {},
        damageSigns: item.variant4?.damageSigns || []
      });
    });
  });
  
  const independentForm2Data = {
    yarusy,
    poroda_characteristics: porodaCharacteristics,
    yarusy_dependent: yarusyDependent,
    poroda_characteristics_dependent: porodaCharacteristicsDependent
  };
  
  // Вспомогательная функция для извлечения ID из объекта справочника (определяем здесь, так как используется ниже)
  const getReferenceId = (value) => {
    if (!value) return '';
    if (typeof value === 'object' && value.id !== undefined) {
      return String(value.id);
    }
    return String(value);
  };
  
  // Преобразуем form2 для to и actual (пока используем одни и те же данные)
  // Преобразуем справочники из объектов в ID (строки)
  const generalPlantationInfo = form2Data.generalPlantationInfo || {};
  const form2Transformed = {
    cnl: getReferenceId(form2Data.cnl),
    kzl: getReferenceId(form2Data.kzl),
    proishozdenie: getReferenceId(form2Data.proishozdenie),
    ozu: getReferenceId(form2Data.ozu),
    nozu: form2Data.nozu || '',
    arenda: getReferenceId(form2Data.arenda),
    vidarenda: getReferenceId(form2Data.vidarenda),
    oopt: getReferenceId(form2Data.oopt),
    noopt: form2Data.noopt || '',
    glu: form2Data.glu || '',
    landAvailability: (() => {
      const v = form2Data.landAvailability;
      if (v === true || v === 'true') return true;
      if (v === false || v === 'false') return false;
      return null;
    })(),
    // Распаковываем поля из generalPlantationInfo на верхний уровень для Form2
    katzem: getReferenceId(generalPlantationInfo.katzem),
    gporoda: getReferenceId(generalPlantationInfo.gporoda),
    bonitet: getReferenceId(generalPlantationInfo.bonitet),
    tiplesa: generalPlantationInfo.tiplesa || '',
    tlu: getReferenceId(generalPlantationInfo.tlu),
    // Сохраняем также generalPlantationInfo для совместимости
    generalPlantationInfo: {
      katzem: getReferenceId(generalPlantationInfo.katzem),
      gporoda: getReferenceId(generalPlantationInfo.gporoda),
      bonitet: getReferenceId(generalPlantationInfo.bonitet),
      tiplesa: generalPlantationInfo.tiplesa || '',
      tlu: getReferenceId(generalPlantationInfo.tlu)
    },
    useActualData: form2Data.useActualData || false,
    itaks2: form2Data.itaks || '' // Преобразуем itaks в itaks2 для Form2
  };
  
  // Преобразуем form3
  // Извлекаем данные из podrost[0] и podlesok[0], если они есть, иначе используем данные напрямую
  const podrostData = form3Data.podrost && form3Data.podrost.length > 0 ? form3Data.podrost[0] : form3Data;
  const podlesokData = form3Data.podlesok && form3Data.podlesok.length > 0 ? form3Data.podlesok[0] : form3Data;
  
  const form3Transformed = {
    podrost: [{
      id: 1,
      sostavpod: podrostData.sostavpod || '',
      vozrpod: podrostData.vozrpod || '',
      hpod: podrostData.hpod || '',
      gustpod: podrostData.gustpod || '',
      sostpod: getReferenceId(podrostData.sostpod) // Преобразуем справочник в ID
    }],
    podlesok: [{
      id: 1,
      sostavpdl: podlesokData.sostavpdl || '',
      gustpdl: podlesokData.gustpdl || '',
      hpdl: podlesokData.hpdl || '',
      sostpdl: podlesokData.sostpdl || '',
      reljeff: getReferenceId(podlesokData.reljeff), // Преобразуем справочник в ID
      reljefeksp: getReferenceId(podlesokData.reljefeksp), // Преобразуем справочник в ID
      reljefuklon: podlesokData.reljefuklon || '',
      eroziavid: getReferenceId(podlesokData.eroziavid), // Преобразуем справочник в ID
      eroziastep: getReferenceId(podlesokData.eroziastep), // Преобразуем справочник в ID
      provlhm: getReferenceId(podlesokData.provlhm), // Преобразуем справочник в ID
      itaks3: form3Data.itaks || podlesokData.itaks3 || '' // Преобразуем itaks в itaks3 для Form3, размещаем внутри podlesok[0]
    }]
  };
  
  // Преобразуем form6 - структура должна быть плоской для Form6 компонента
  const recommendedMeasuresLoaded = (form6Data.recommendedActivities || []).map((activity, index) => ({
    id: index + 1, // Добавляем id для каждого элемента
    rekvidmer: getReferenceId(activity.rekvidmer), // Преобразуем объект в ID (строку)
    srokmzl: activity.srokmzl || '',
    srekme: activity.srekmer || '',
    primechmzl: activity.primechmzl || ''
  }));
  const form6Transformed = {
    recommended_measures: recommendedMeasuresLoaded,
    // Наличие рекомендованных мероприятий — из поля карточки; у карточек, где его
    // ещё нет, выводится по самим мероприятиям
    has_recommended_measures: hasRecommendedMeasuresFromCard(
      form6Data.activities_required,
      recommendedMeasuresLoaded
    ),
    itaks6: form6Data.itaks || '', // Преобразуем itaks в itaks6 для Form6
    // Сохраняем данные очагов для использования в Form4
    selectedOchagCodes: form6Data.selectedOchagCodes || [],
    vidvo: form6Data.vidvo || {}
  };
  
  // Преобразуем form1 - нужно преобразовать latitude/longitude в lat/lon и справочники в правильный формат
  const form1Transformed = {
    ...form1Data,
    // Преобразуем latitude и longitude в lat и lon (объекты)
    lat: form1Data.latitude ? { 
      lat: parseFloat(form1Data.latitude) || 0, 
      lng: parseFloat(form1Data.longitude) || 0 
    } : null,
    lon: form1Data.longitude ? { 
      lat: parseFloat(form1Data.latitude) || 0, 
      lng: parseFloat(form1Data.longitude) || 0 
    } : null,
    // Преобразуем справочники: srf (полный объект), cel, ekolzona - в ID (строки)
    // refToFullObject/refToName/refToId поддерживают новый { handbookId, id }, старый полный объект и строку
    srf: refToFullObject(form1Data.srf, handbooks),
    cel: refToId(form1Data.cel),
    ekolzona: refToId(form1Data.ekolzona),
    // Преобразуем зависимые поля: filial, lvo, ulvo, urc - в строки (названия)
    filial: refToName(form1Data.filial, handbooks),
    lvo: refToName(form1Data.lvo, handbooks),
    ulvo: refToName(form1Data.ulvo, handbooks),
    urc: refToName(form1Data.urc, handbooks),
    lvoId: refToId(form1Data.lvo),
    ulvoId: refToId(form1Data.ulvo),
    urcId: refToId(form1Data.urc),
    // Преобразуем поля выдела: vid, s -> sga, lpvid, slp
    vid: form1Data.vid || '',
    sga: form1Data.s || '', // s с сервера -> sga в форме (площадь выдела)
    lpvid: form1Data.lpvid || '',
    slp: form1Data.slp || '',
    // Преобразуем itaks (информация пользователя) в itaks1 для Form1
    itaks1: form1Data.itaks || '',
    // Преобразуем дату из формата DD.MM.YYYY в формат yyyy-MM-dd для input type="date"
    data: form1Data.data || '',
    // Дата проведения ВНН — в ISO для input type="date".
    // Откат на data — для карточек, сохранённых веб-клиентом до перехода на datar:
    // там дата работ лежала в data, иначе обязательное поле оказалось бы пустым.
    datar: convertDateToInputFormat(form1Data.datar || form1Data.data || ''),
    // Удаляем старые поля
    latitude: undefined,
    longitude: undefined,
    s: undefined, // Удаляем s, так как используем sga
    itaks: undefined // Удаляем itaks, так как используем itaks1
  };
  
  // Удаляем undefined поля
  Object.keys(form1Transformed).forEach(key => {
    if (form1Transformed[key] === undefined) {
      delete form1Transformed[key];
    }
  });
  
  // Преобразуем form4 - преобразуем itaks в itaks4
  // Преобразуем form4 - нужно преобразовать charochag из объекта в ID
  // Также нужно преобразовать damageSigns из porodaCharacteristicsDependent в savedModalData
  const form4SavedModalData = {};
  
  // Преобразуем damageSigns из porodaCharacteristicsDependent в savedModalData
  // Ключ - это индекс + 1 (как ID элемента в Form4.data), значение - массив преобразованных damageSigns
  if (independentForm2Data && independentForm2Data.poroda_characteristics_dependent) {
    independentForm2Data.poroda_characteristics_dependent.forEach((item, index) => {
      const damageSigns = item.damageSigns || [];
      if (damageSigns.length > 0) {
        // Преобразуем damageSigns в формат, который ожидает Form4
        const transformedDamageSigns = damageSigns.map((sign, signIndex) => {
          // Преобразуем priznList в массив priznaki (новый формат)
          let priznaki = [];
          if (sign.priznList && Array.isArray(sign.priznList) && sign.priznList.length > 0) {
            // Преобразуем все элементы из priznList в массив ID признаков
            priznaki = sign.priznList.map(prizn => {
              // Используем id, если есть, иначе code
              return refToId(prizn);
            }); // Пустые элементы сохраняются для проверки, как в МП.
          }
          
          return {
            id: signIndex + 1,
            prichina: sign.prichina?.id ? String(sign.prichina.id) : '',
            godpovr: sign.godpovr || '',
            priznaki: priznaki, // Используем новый формат - массив
            dolaprizn: sign.dolaprizn || '',
            specificDolaPrizn: sign.specificDolaPrizn || ''
          };
        });
        
        // Используем index + 1 как ID элемента (как в Form4)
        form4SavedModalData[index + 1] = transformedDamageSigns;
      }
    });
  }
  
  const form4Transformed = {
    ...form4Data,
    // Сводка должна работать и до первого открытия формы 4.
    data: Array.isArray(form4Data.data) && form4Data.data.some(row => row.yarus || row.kodporoda || row.dolap)
      ? form4Data.data
      : independentForm2Data.poroda_characteristics_dependent.map((item, index) => {
          const variant = item.variant4 || {};
          const row = {
            id: index + 1, yarus: item.yarus || '', dolap: item.dolap || '',
            kodporoda: refToId(item.kodporodato), vozr: item.vozr || '', hsr: item.hsr || '', dsr: item.dsr || '',
            osldolap: variant.catII ?? 0, sosldolap: variant.catIII ?? 0, usdolap: variant.catIV ?? 0,
            svsuhdolap: variant.catVa ?? 0, svvtrdolap: variant.catVb ?? 0, svburdolap: variant.catVc ?? 0,
            stsuhdolap: variant.catVd ?? 0, stvtrdolap: variant.catVe ?? 0, stburdolap: variant.catVf ?? 0,
          };
          return { ...row, bpodolap: calculateIZdor(row) };
        }),
    charochag: getReferenceId(form4Data.charochag), // Преобразуем справочник в ID
    itaks4: form4Data.itaks || '',
    savedModalData: Object.keys(form4SavedModalData).length > 0 
      ? form4SavedModalData 
      : (form4Data.savedModalData || {})
  };
  if (form4Transformed.itaks !== undefined) {
    delete form4Transformed.itaks;
  }
  
  // Преобразуем form5 - преобразуем itaks в itaks5
  // Form5 на сервере хранится как { harmfulOrganisms: [], itaks: '' }
  // Но Form5 компонент ожидает структуру с полем data (массив строк таблицы)
  // Поэтому нужно создать data из independentForm2Data.poroda_characteristics_dependent
  let form5DataArray = [];
  
  // Если data уже есть в form5Data (например, при загрузке из localStorage), используем его
  if (form5Data.data && Array.isArray(form5Data.data) && form5Data.data.length > 0) {
    form5DataArray = form5Data.data;
  } else if (independentForm2Data && independentForm2Data.poroda_characteristics_dependent) {
    // Иначе создаем data из independentForm2Data.poroda_characteristics_dependent
    form5DataArray = independentForm2Data.poroda_characteristics_dependent.map((item, index) => {
      // Извлекаем variant5 для получения процентов повреждения
      const variant5 = item.variant5 || {};
      
      // Преобразуем kodporodato из объекта в ID (строку), если это объект
      let kodporodaId = item.kodporodato;
      if (typeof kodporodaId === 'object' && kodporodaId !== null) {
        kodporodaId = kodporodaId.id ? String(kodporodaId.id) : '';
      } else if (kodporodaId !== null && kodporodaId !== undefined) {
        kodporodaId = String(kodporodaId);
      } else {
        kodporodaId = '';
      }
      
      // Извлекаем причины из variant5.prichini
      // Преобразуем массив объектов причин в массив ID (числа)
      let causes = [];
      if (variant5.prichini && Array.isArray(variant5.prichini)) {
        causes = variant5.prichini
          .map(prichina => {
            if (typeof prichina === 'object' && prichina !== null && prichina.id !== undefined) {
              return typeof prichina.id === 'string' ? parseInt(prichina.id, 10) : prichina.id;
            } else if (typeof prichina === 'number') {
              return prichina;
            } else if (typeof prichina === 'string') {
              const parsed = parseInt(prichina, 10);
              return isNaN(parsed) ? null : parsed;
            }
            return null;
          })
          .map(id => id !== null && !isNaN(id) ? id : null);
      }
      
      return {
        id: index + 1,
        yarus: item.yarus || '',
        dolap: item.dolap || '',
        kodporoda: kodporodaId,
        vozr: item.vozr || '',
        hsr: item.hsr || '',
        dsr: item.dsr || '',
        objedola0: 100, // Вычисляемое поле
        objedola25: variant5.cat25 || '',
        objedola50: variant5.cat50 || '',
        objedola75: variant5.cat75 || '',
        objedola100: variant5.cat100 || '',
        savedModalData: {}, // Данные модального окна будут загружены отдельно, если есть
        causes: causes // Сохраняем выбранные причины
      };
    });
  }
  
  // Извлекаем ochagPrichina из form5Data.prichina (если есть объект) или из form5Data.ochagPrichina (если есть ID)
  let ochagPrichinaId = '';
  let prichinaCode = null;
  if (form5Data.prichina) {
    // Если prichina - это объект с id, извлекаем id и code
    if (typeof form5Data.prichina === 'object' && form5Data.prichina.id !== undefined) {
      ochagPrichinaId = String(form5Data.prichina.id);
      prichinaCode = form5Data.prichina.code || null;
      // Новый формат { handbookId, id } без code — разрешаем из справочника причин
      if (!prichinaCode) {
        const fullPrichina = refToFullObject(form5Data.prichina, handbooks);
        if (fullPrichina && typeof fullPrichina === 'object') {
          prichinaCode = fullPrichina.code || null;
        }
      }
    } else if (typeof form5Data.prichina === 'string' || typeof form5Data.prichina === 'number') {
      // Если prichina - это ID (строка или число)
      ochagPrichinaId = String(form5Data.prichina);
    }
  } else if (form5Data.ochagPrichina) {
    // Если есть ochagPrichina, используем его
    ochagPrichinaId = String(form5Data.ochagPrichina);
  }
  
  // Если нет кода причины, но есть ID, пытаемся получить код из справочника
  // (для этого нужны handbooks, но они не передаются в loadDocumentDataFromServer)
  // Поэтому код будет получен позже, если нужно
  
  // Извлекаем ochagFaza и ochagTip из form6Data.vidvo по коду причины
  // Приоритет: сохраненные значения в form5Data > значения из form6Data.vidvo
  let ochagFaza = form5Data.ochagFaza || '';
  let ochagTip = form5Data.ochagTip || '';
  
  // Если нет сохраненных значений, пытаемся взять из form6Data.vidvo
  if (prichinaCode && form6Data && form6Data.vidvo && (!ochagFaza || !ochagTip)) {
    const vidvoEntry = form6Data.vidvo[String(prichinaCode)];
    if (vidvoEntry) {
      // Преобразуем фазу из формата сервера в формат формы (только если нет сохраненного значения)
      if (!ochagFaza && vidvoEntry.ochagPhasa) {
        const phasaMapping = {
          'INITIAL': 'начальная',
          'POPULATION_GROWTH': 'нарастания численности',
          'OUTBREAK': 'собственно вспышка',
          'CRISIS': 'кризис',
          'EMERGING': 'возникающий',
          'ACTIVE': 'действующий',
          'FADING': 'затухающий'
        };
        if (phasaMapping[vidvoEntry.ochagPhasa]) {
          ochagFaza = phasaMapping[vidvoEntry.ochagPhasa];
        } else {
          // Если уже в русском формате, используем как есть
          ochagFaza = vidvoEntry.ochagPhasa;
        }
      }
      
      // Преобразуем тип из формата сервера в формат формы (только если нет сохраненного значения)
      if (!ochagTip && vidvoEntry.ochagType) {
        if (vidvoEntry.ochagType === 'EPISODIC') {
          ochagTip = 'эпизодический';
        } else if (vidvoEntry.ochagType === 'CHRONIC') {
          ochagTip = 'хронический';
        } else {
          // Если уже в русском формате, используем как есть
          ochagTip = vidvoEntry.ochagType;
        }
      }
    }
  }
  
  const form5Transformed = {
    data: form5DataArray,
    // Виды вредных организмов веб-интерфейс не редактирует, но обязан пронести
    // их через состояние: сборка запроса читает это поле, и без него любое
    // сохранение из веба затирало бы данные, записанные другим клиентом.
    harmfulOrganisms: Array.isArray(form5Data.harmfulOrganisms) ? form5Data.harmfulOrganisms : [],
    itaks5: form5Data.itaks || '',
    savedModalData: form5Data.savedModalData || {},
    ochagPrichina: ochagPrichinaId,
    ochagVidVO: form5Data.ochagVidVO || '',
    ochagFaza: ochagFaza,
    ochagTip: ochagTip
  };
  
  // form6Transformed уже содержит itaks6, дополнительных преобразований не требуется
  
  // Извлекаем название документа из data.name или из верхнего уровня serverData
  // Также проверяем, есть ли name в верхнем уровне data (если есть двойное вложение)
  let documentName = '';
  if (serverData.data && serverData.data.name) {
    documentName = serverData.data.name;
  } else if (documentData.name) {
    documentName = documentData.name;
  } else if (serverData.name) {
    documentName = serverData.name;
  }
  
  return {
    form1: form1Transformed,
    form2: {
      to: form2Transformed,
      actual: form2Transformed
    },
    form3: form3Transformed,
    form4: form4Transformed,
    form5: form5Transformed,
    form6: form6Transformed,
    independentForm2Data,
    selectedForm45: optionalTab === 'FORM_4' ? 4 : 5,
    documentName: documentName
  };
};

